import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth, ROLES } from '../../../contexts/AuthContext';
import UserRoleSelector from '../components/UserRoleSelector';
import ModulePermissionsTable from '../components/ModulePermissionsTable';
import {
  getAllModules,
  getCompanyRoles,
  getCompanyUsers,
  getRolePermissions,
  getUserPermissions,
  updateRolePermissions,
  updateUserPermissions,
  resetUserToRoleDefaults,
  bulkUpdateRolePermissions,
  bulkUpdateUserPermissions,
} from '../services/accessManagementService';

/**
 * AccessManagementPage
 * Main page for managing role and user permissions (CompanyAdmin only)
 */
const AccessManagementPage = () => {
  const { t } = useTranslation();
  const { isCompanyAdmin, role } = useAuth();

  // State Management
  const [mode, setMode] = useState('user'); // 'user' or 'role' - User first by default
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // UI State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
  });

  // Load all modules
  const loadModules = async () => {
    try {
      setLoading(true);
      const response = await getAllModules();
      // API returns { payload: [...] }
      const modulesArray = response?.payload || [];
      setModules(modulesArray);
    } catch (error) {
      setModules([]);
      showSnackbar(t('access_management.error_loading_modules'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load all roles
  const loadRoles = async () => {
    try {
      const response = await getCompanyRoles();
      // API returns { payload: ["Manager", "CompanyAdmin", ...] }
      const rolesArray = response?.payload || [];
      // Transform string array to objects with id and name
      const transformedRoles = rolesArray.map(roleName => ({
        id: roleName,
        name: roleName,
        userCount: 0 // TODO: Get actual count from API
      }));
      setRoles(transformedRoles);
      if (transformedRoles.length > 0 && !selectedRole) {
        setSelectedRole(transformedRoles[0].id);
      }
    } catch (error) {
      setRoles([]);
      showSnackbar(t('access_management.error_loading_roles'), 'error');
    }
  };

  // Load all users
  const loadUsers = async () => {
    try {
      const response = await getCompanyUsers();
      // API returns { payload: [{Id, Email, FullName, Roles, ...}] }
      const usersArray = response?.payload || [];
      // Transform to expected format - use Email as ID if Id is not available
      const transformedUsers = usersArray.map(user => {
        const userId = user.Id || user.id || user.Email || user.email;
        return {
          id: userId,
          email: user.Email || user.email,
          name: user.FullName || user.Name || user.name || user.Email || 'Unknown',
          role: (user.Roles && user.Roles.length > 0) ? user.Roles[0] : (user.Role || user.role || 'User')
        };
      });
      setUsers(transformedUsers);
    } catch (error) {
      setUsers([]);
      showSnackbar(t('access_management.error_loading_users'), 'error');
    }
  };

  // Load role permissions
  const loadRolePermissions = async (roleId) => {
    try {
      setLoading(true);
      const data = await getRolePermissions(roleId);
      const perms = data?.permissions || data?.payload || {};
      setPermissions(perms);
      setOriginalPermissions(perms);
    } catch (error) {
      // Set empty permissions on error
      setPermissions({});
      setOriginalPermissions({});
      showSnackbar(t('access_management.error_loading_permissions'), 'warning');
    } finally {
      setLoading(false);
    }
  };

  // Load user permissions
  const loadUserPermissions = async (userId) => {
    try {
      setLoading(true);
      const data = await getUserPermissions(userId);
      const perms = data?.permissions || data?.payload || {};
      setPermissions(perms);
      setOriginalPermissions(perms);
    } catch (error) {
      // Set empty permissions on error
      setPermissions({});
      setOriginalPermissions({});
      showSnackbar(t('access_management.error_loading_permissions'), 'warning');
    } finally {
      setLoading(false);
    }
  };

  // Handle mode change
  const handleModeChange = (newMode) => {
    if (hasChanges) {
      setConfirmDialog({
        open: true,
        title: t('access_management.unsaved_changes'),
        message: t('access_management.confirm_discard_changes'),
        action: () => {
          setMode(newMode);
          setHasChanges(false);
          setPermissions({});
          setOriginalPermissions({});
          // Reset selections when switching modes
          if (newMode === 'user') {
            setSelectedUser(null);
          } else {
            setSelectedRole(roles.length > 0 ? roles[0].id : null);
          }
        },
      });
    } else {
      setMode(newMode);
      setPermissions({});
      setOriginalPermissions({});
      // Reset selections when switching modes
      if (newMode === 'user') {
        setSelectedUser(null);
      } else {
        setSelectedRole(roles.length > 0 ? roles[0].id : null);
      }
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);

      if (mode === 'role' && selectedRole) {
        await updateRolePermissions(selectedRole, permissions);
        showSnackbar(t('access_management.role_permissions_updated'), 'success');
      } else if (mode === 'user' && selectedUser) {
        await updateUserPermissions(selectedUser, permissions);
        showSnackbar(t('access_management.user_permissions_updated'), 'success');
      } else {
        return;
      }

      setOriginalPermissions(permissions);
      setHasChanges(false);
    } catch (error) {
      showSnackbar(t('access_management.error_saving_permissions'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle reset user to role defaults
  const handleResetToDefaults = async () => {
    if (mode !== 'user' || !selectedUser) return;

    setConfirmDialog({
      open: true,
      title: t('access_management.reset_to_defaults'),
      message: t('access_management.confirm_reset_user'),
      action: async () => {
        try {
          setSaving(true);
          await resetUserToRoleDefaults(selectedUser);
          await loadUserPermissions(selectedUser);
          showSnackbar(t('access_management.user_reset_success'), 'success');
        } catch (error) {
          showSnackbar(t('access_management.error_resetting_user'), 'error');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    setPermissions(originalPermissions);
    setHasChanges(false);
    showSnackbar(t('access_management.changes_discarded'), 'info');
  };

  // Show snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Close confirm dialog
  const handleCloseDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  // Confirm action
  const handleConfirmAction = () => {
    if (confirmDialog.action) {
      confirmDialog.action();
    }
    handleCloseDialog();
  };

  // Load initial data
  useEffect(() => {
    loadModules();
    loadRoles();
    loadUsers();
  }, []);

  // Load permissions when selection changes
  useEffect(() => {
    if (mode === 'role' && selectedRole) {
      loadRolePermissions(selectedRole);
    } else if (mode === 'user' && selectedUser) {
      loadUserPermissions(selectedUser);
    } else {
      setPermissions({});
      setOriginalPermissions({});
    }
  }, [mode, selectedRole, selectedUser]);

  // Check for changes
  useEffect(() => {
    const changed = JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
    setHasChanges(changed);
  }, [permissions, originalPermissions]);

  // ✅ Temporarily disabled - Allow all users to access (original: only CompanyAdmin)
  // Redirect if not CompanyAdmin
  // if (!isCompanyAdmin) {
  //   return (
  //     <Box sx={{ p: 3 }}>
  //       <Alert severity="error">
  //         {t('access_management.unauthorized')}
  //       </Alert>
  //     </Box>
  //   );
  // }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1400px', mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <SecurityIcon sx={{ fontSize: 40, color: '#667eea' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {t('access_management.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('access_management.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Role Badge */}
        <Chip
          label={`${t('access_management.logged_in_as')}: ${role}`}
          sx={{
            bgcolor: '#ff9800',
            color: 'white',
            fontWeight: 600,
            mt: 1,
          }}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Main Content Grid */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Left Panel - Selector */}
        <Paper
          sx={{
            width: { xs: '100%', md: 350 },
            p: 3,
            height: 'fit-content',
            position: { md: 'sticky' },
            top: 20,
          }}
        >
          <UserRoleSelector
            mode={mode}
            onModeChange={handleModeChange}
            roles={roles}
            users={users}
            selectedRole={selectedRole}
            selectedUser={selectedUser}
            onRoleChange={(newRole) => {
              setSelectedRole(newRole);
            }}
            onUserChange={(newUser) => {
              setSelectedUser(newUser);
            }}
            loading={loading}
          />

          {/* Action Buttons */}
          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges || saving || loading}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                py: 1.5,
              }}
            >
              {saving ? t('access_management.saving') : t('access_management.save_changes')}
            </Button>

            {hasChanges && (
              <Button
                variant="outlined"
                onClick={handleDiscardChanges}
                disabled={saving || loading}
                fullWidth
              >
                {t('access_management.discard_changes')}
              </Button>
            )}

            {mode === 'user' && selectedUser && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<RefreshIcon />}
                onClick={handleResetToDefaults}
                disabled={saving || loading}
                fullWidth
              >
                {t('access_management.reset_to_defaults')}
              </Button>
            )}
          </Box>

          {/* Changes Indicator */}
          {hasChanges && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('access_management.unsaved_changes_warning')}
            </Alert>
          )}
        </Paper>

        {/* Right Panel - Permissions Table */}
        <Box sx={{ flex: 1 }}>
          {(mode === 'role' && !selectedRole) || (mode === 'user' && !selectedUser) ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <SecurityIcon sx={{ fontSize: 60, color: '#e0e0e0', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {mode === 'user' 
                  ? (t('access_management.select_user_first') || 'Please select a user first')
                  : t('access_management.select_target')
                }
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {mode === 'user'
                  ? (t('access_management.user_selection_required') || 'Select a user from the dropdown to view and manage their permissions')
                  : t('access_management.select_target_description')
                }
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ p: 0, overflow: 'hidden' }}>
              {/* Table Header */}
              <Box
                sx={{
                  p: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  {t('access_management.module_permissions')}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  {t('access_management.configure_access')}
                </Typography>
              </Box>

              {/* Permissions Table */}
              <Box sx={{ p: 3 }}>
                <ModulePermissionsTable
                  modules={modules}
                  permissions={permissions}
                  onChange={setPermissions}
                  loading={loading}
                  disabled={saving}
                  mode={mode}
                />
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCloseDialog}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
          <Button onClick={handleConfirmAction} variant="contained" color="primary">
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessManagementPage;
